import type { RatingUnitId } from "./index";
import { RATING_SCALE_MANIFEST as manifest, RATING_UNIT_IDS } from "./registry";

/**
 * The supported rating scales (owner decision B, 2026-09-24): the units a new
 * or edited measure may use, each with its respondent point count, whether its
 * points take labels, and its name in prose. Python reads the same file
 * (`apps/python-api/models/questionnaire/rating_scales.py`). Every TypeScript
 * reader of that list derives from here.
 *
 * A stored draft keeps the full `RatingUnitId` vocabulary (`rating_1_7` and
 * `rating_0_100` still load); that is the stored-data type, not this list.
 * Its bounds live in the same file's `units`, read by `registry.ts`, which
 * also checks the file's schema version.
 */
export interface RatingScaleFacts {
  /** Respondent points: 5, 10, 11 for 0 to 10, 2 for yes/no. */
  points: number;
  /** Whether each point may carry a label; a scale without them uses its anchors. */
  pointLabels: boolean;
  /** The scale's name in prose ("1 to 5"). */
  label: string;
}

/** A unit a new or edited measure may use. */
export type SupportedRatingUnitId = keyof typeof manifest.scales;

// Every supported unit is a stored unit: a key outside RatingUnitId fails here.
const SUPPORTED_UNITS_ARE_RATING_UNITS: ReadonlyArray<RatingUnitId> =
  Object.keys(manifest.scales) as Array<SupportedRatingUnitId>;

if (
  SUPPORTED_UNITS_ARE_RATING_UNITS.some(
    (unit) => !(RATING_UNIT_IDS as ReadonlyArray<string>).includes(unit),
  )
) {
  throw new TypeError(
    "Questionnaire rating-scale registry does not match RatingUnitId.",
  );
}

export const RATING_SCALES: Readonly<
  Record<SupportedRatingUnitId, RatingScaleFacts>
> = Object.freeze(manifest.scales);

/** In registry order, the order the provider schemas list them. */
export const SUPPORTED_RATING_UNIT_IDS = Object.freeze(
  Object.keys(RATING_SCALES) as Array<SupportedRatingUnitId>,
);

/** Each supported unit's respondent point count. */
export const RATING_SCALE_POINTS: Readonly<Record<string, number>> =
  Object.freeze(
    Object.fromEntries(
      SUPPORTED_RATING_UNIT_IDS.map((unit) => [unit, RATING_SCALES[unit].points]),
    ),
  );

/** The supported units whose points take respondent labels. */
export const POINT_LABEL_RATING_UNIT_IDS = Object.freeze(
  SUPPORTED_RATING_UNIT_IDS.filter((unit) => RATING_SCALES[unit].pointLabels),
);

/**
 * The supported units a respondent answers on point buttons: every supported
 * scale but a binary one, which stays a yes/no choice list. Derived from the
 * same registry (`units[unit].normalization`), so a supported scale without
 * point labels is still a point scale. A stored unit outside the supported
 * list (`rating_0_100`) is not one.
 */
export const POINT_BUTTON_RATING_UNIT_IDS = Object.freeze(
  SUPPORTED_RATING_UNIT_IDS.filter(
    (unit) => manifest.units[unit].normalization !== "binary",
  ),
);

/** Whether a compiled unit is answered on point buttons (`POINT_BUTTON_RATING_UNIT_IDS`). */
export function isPointButtonRatingUnit(value: unknown): boolean {
  return (
    typeof value === "string" &&
    (POINT_BUTTON_RATING_UNIT_IDS as ReadonlyArray<string>).includes(value)
  );
}

export function isSupportedRatingUnitId(
  value: unknown,
): value is SupportedRatingUnitId {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(RATING_SCALES, value)
  );
}

/** `a`, `a and b`, `a, b, and c`: the same join as Python's `english_list`. */
export function englishList(
  items: ReadonlyArray<string>,
  conjunction: "and" | "or" = "and",
): string {
  if (items.length < 3) return items.join(` ${conjunction} `);
  return `${items.slice(0, -1).join(", ")}, ${conjunction} ${items[items.length - 1]}`;
}

const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six",
  "seven", "eight", "nine", "ten", "eleven", "twelve",
] as const;

/** `2` → `two` up to twelve, digits above: the same as Python's `english_number`. */
export function englishNumber(value: number): string {
  return Number.isInteger(value) && value >= 0 && value < NUMBER_WORDS.length
    ? NUMBER_WORDS[value]
    : String(value);
}

/** "1 to 5, 1 to 10, 0 to 10, and yes/no": the supported scales, as a reply names them. */
export const SUPPORTED_RATING_SCALES_TEXT = englishList(
  SUPPORTED_RATING_UNIT_IDS.map((unit) => RATING_SCALES[unit].label),
);
